import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Container, makeStyles } from '@material-ui/core';
import { Add as PlusIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import FooterButton from 'components/FooterButton';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { parseFiltersForActionTemplates } from 'config/api/actionLibrary/actionLibrary.parsers';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useSessionFilters from 'hooks/useSessionFilters';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import PATHS from 'router/PATHS';
import { getAreaFilter, getCriticalityFilter, getCreatorFilter, getStatusFilter } from 'services/_filters/parsers/actionTemplateFilters';
import type { FilterState, AvailableFilters } from 'types/ActionLibrary';

import ActionTemplatesTable from './_components/ActionTemplatesTable/ActionTemplatesTable';
import EmptyState from './_components/EmptyState/EmptyState';
import ErrorState from './_components/ErrorState/ErrorState';

const useStyles = makeStyles(theme => ({
  createButton: {
    marginBottom: theme.spacing(2),
  },
  iconInButton: {
    marginRight: theme.spacing(1),
  },
}));

const ActionLibraryPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const styles = useStyles();
  const { showHelpDialog } = useHelpDialogContext();

  const [filters, setFilters] = useSessionFilters<FilterState>('ACTION_LIBRARY_FILTERS', {});
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const [loadingFiltersError, setLoadingFiltersError] = useState<Error | null>(null);
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({
    key: null,
    direction: null,
  });
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const pageSize = 20;

  const onSort = (key: string, direction: string) => {
    setSort({ key, direction });
  };

  const queryParams = useMemo(() => {
    // Build ordering parameter for API
    let ordering: string | undefined;
    if (sort.key && sort.direction) {
      const prefix = sort.direction === 'desc' ? '-' : '';
      ordering = `${prefix}${sort.key}`;
    }

    // Parse filters from frontend format (camelCase) to API format (snake_case)
    const parsedFilters = parseFiltersForActionTemplates(filters);

    return {
      limit: pageSize,
      offset: currentOffset,
      ...(searchTerm && { search: searchTerm }),
      ...(ordering && { ordering }),
      ...parsedFilters,
    };
  }, [filters, sort, currentOffset, searchTerm, pageSize]);

  const {
    data: queryData,
    isLoading: loading,
    error,
    refetch,
  } = useQuery(
    [QUERY_KEYS.ACTION_TEMPLATES_GET_ALL, queryParams],
    async () => {
      const { data } = await actionTemplatesApi.getAllActionTemplates(queryParams);
      return data;
    },
    {
      enabled: !!availableFilters,
      keepPreviousData: true,
      refetchInterval: 5000,
    },
  );

  const tableData = queryData?.results || [];
  const hasNextPage = queryData?.next !== null;

  const getTableData = useCallback(() => {
    setCurrentOffset(prev => prev + pageSize);
  }, [pageSize]);

  const refreshData = useCallback(() => {
    if (currentOffset === 0) {
      refetch();
    } else {
      setCurrentOffset(0);
    }
  }, [currentOffset, refetch]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [areaFilter, criticalityFilter, creatorFilter, statusFilter] = await Promise.all([
          getAreaFilter(),
          getCriticalityFilter(),
          getCreatorFilter(),
          getStatusFilter(),
        ]);

        setAvailableFilters({
          area: areaFilter,
          subarea: {
            sectionName: action_library_messages.filters.subarea,
            filters: [],
          },
          status: statusFilter,
          criticality: criticalityFilter,
          creator: creatorFilter,
        });
      } catch (err) {
        setLoadingFiltersError(err as Error);
      }
    };

    loadFilters();
  }, []);

  const { data: areasData } = useQuery('actionTemplatesAreas', () => actionTemplatesApi.getAreas(), { enabled: !!availableFilters });

  // Update subarea filter when area filter changes – use getAreas() (same source as create template) so custom subareas are visible (AC 3.1)
  useEffect(() => {
    if (!availableFilters) return;

    const selectedAreas = filters.area || [];
    const emptySubarea = {
      sectionName: action_library_messages.filters.subarea,
      filters: [] as Array<{ id: string | number; name: string }>,
    };

    if (selectedAreas.length === 0) {
      setAvailableFilters(prev => {
        if (!prev) return null;
        if (prev.subarea.filters.length === 0) return prev;
        return { ...prev, subarea: emptySubarea };
      });
      setFilters(prev => {
        if (!prev.subarea) return prev;
        const newFilters = { ...prev };
        delete newFilters.subarea;
        return newFilters;
      });
      return;
    }

    const areas = areasData?.data?.areas;
    if (!areas) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areaIds = selectedAreas.map(area => ((area as any)?.id || (area as any)?.name || area) as string);
    const allFilters: Array<{ id: string | number; name: string }> = [];
    const seenIds = new Set<string | number>();

    areaIds.forEach(areaId => {
      const selectedArea = areas.find(a => String(a.value) === String(areaId));
      selectedArea?.subareas?.forEach(s => {
        const id = s.value;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          allFilters.push({ id, name: s.label });
        }
      });
    });

    const combinedSubareaFilter = { ...emptySubarea, filters: allFilters };
    setAvailableFilters(prev => {
      if (!prev) return null;
      if (JSON.stringify(prev.subarea.filters) === JSON.stringify(combinedSubareaFilter.filters)) return prev;
      return { ...prev, subarea: combinedSubareaFilter };
    });
  }, [filters.area, availableFilters, areasData?.data]);

  // Reset offset when filters or sort change
  useEffect(() => {
    setCurrentOffset(0);
  }, [filters, sort, searchTerm]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    // Offset reset is handled by useEffect
  };

  const handleCreateClick = () => {
    history.push(PATHS.ACTION_LIBRARY_CREATE);
  };

  const openHelpDialog = () => {
    showHelpDialog({ title: t(action_library_messages.help.title), body: t(action_library_messages.help.body) });
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');
    // Offset reset is handled by useEffect
  };

  if (error || loadingFiltersError) {
    return (
      <Container>
        <ErrorState onRetry={() => window.location.reload()} />
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          rightAdornment={
            <>
              <TextButton onClick={openHelpDialog}>{t(general_messages.how_it_works)}</TextButton>
              <ColoredButton
                className={styles.createButton}
                customColor='secondary'
                disabled={false}
                icon={false}
                onClick={handleCreateClick}
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                <PlusIcon className={styles.iconInButton} fontSize='small' />
                {t(action_library_messages.create_button)}
              </ColoredButton>
            </>
          }
        >
          {t(action_library_messages.page_title)}
        </ManagePageHeader>

        {!availableFilters ? (
          <Loader inner />
        ) : (
          <>
            <ActionTemplatesTable
              data={tableData}
              filtersService={{ availableFilters, filters, setFilters }}
              loading={loading}
              onSearch={handleSearch}
              onSort={onSort}
              refreshData={refreshData}
              searchValue={searchTerm}
              sort={sort}
            />
            {tableData && tableData.length === 0 && !loading && <EmptyState onResetFilters={handleResetFilters} />}

            {hasNextPage && (
              <FooterButton className='' loading={loading} onClick={getTableData}>
                {t(action_library_messages.table.show_more)}
              </FooterButton>
            )}
          </>
        )}
      </Container>
    </ErrorBoundary>
  );
};

export default ActionLibraryPage;
